/*
  Outcome Options Modal

  Modal for configuring edge options for source workflow nodes.
  Adapted from the legacy journey editor's outcome-options-modal.

  Allows configuration of:
  - Opens Application (for application journeys)
  - Opens Alert (for alert journeys)
  - Opens Investigation (with type selection and creation)
  - Promotes to Case
*/

import noop from 'lodash/noop';
import cloneDeep from 'lodash/cloneDeep';
import {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import styled from 'styled-components';
import type { Option } from '@usealloy/component-library';
import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Icon,
  IconName,
  Label,
  Modal,
  MultiSelect,
  Tabs,
  Text,
  useSnackBar,
  Tooltip,
  JourneyNode,
  JourneyBadge,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import apiCalls from '~/helpers/apiCalls';

import type { Edge, Node } from '../../types';

interface EdgeConfig {
  id: string;
  opensApplication?: boolean;
  opensAlert?: boolean;
  opensInvestigation?: boolean;
  investigationType?: string | null;
  promotesToCase?: boolean;
  investigationTypeUseCase?: string | null;
  isNewInvestigationType?: boolean;
  investigationError?: string | null;
}

interface OutcomeOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (configsByEdge: EdgeConfig[]) => void;
  node: Node;
  nodeEdges: Edge[];
  journeyType?: string;
}

const NODE_TYPE_ICON_MAP = {
  workflow: IconName.Workflows,
  source_workflow: IconName.Workflows,
  review: IconName.Review,
  action: IconName.Action,
  outcome: IconName.Journey,
};

const getIconBackgroundColor = (icon: IconName) => {
  switch (icon) {
    case IconName.ApplicationReview:
    case IconName.BoltAlert:
      return 'var(--cl-juicyBlue-500)';
    case IconName.FolderOpenInvestigation:
      return 'var(--cl-yellow-300)';
    case IconName.Escalate:
      return 'var(--cl-red-300)';
    default:
      return 'transparent';
  }
};

const StyledBadge = styled(Badge) <{ icon: IconName }>`
  div {
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background-color: ${props => getIconBackgroundColor(props.icon)};
  }
`;

const StyledEdge = styled.div`
  position: absolute;
  width: 100%;
  height: 55%;
  border-bottom: 2px solid var(--cl-gray-500);
  z-index: -1;
`;

const getEdgeConfigs = (nodeEdges: Edge[]): EdgeConfig[] => nodeEdges.map(
  (edge): EdgeConfig => ({
    id: edge.id,
    opensApplication: Boolean(edge.config?.opensApplication),
    opensAlert: Boolean(edge.config?.opensAlert),
    opensInvestigation: Boolean(edge.config?.opensInvestigation),
    investigationType: (edge.config?.investigationType as string) ?? null,
    promotesToCase: Boolean(edge.config?.promotesToCase),
  }),
);

interface ModalBodyProps {
  journeyType: string;
  isSourceWorkflow: boolean;
  node: Node;
  edge: Edge;
  mutableCurrentEdgeConfig: EdgeConfig;
  setMutableCurrentEdgeConfig: (config: EdgeConfig) => void;
  investigationTypesCreatePermission: boolean;
  investigationTypes: Array<{ name: string; useCase: string }>;
}

const ModalBody = ({
  journeyType,
  isSourceWorkflow,
  node,
  edge,
  mutableCurrentEdgeConfig,
  setMutableCurrentEdgeConfig,
  investigationTypesCreatePermission,
  investigationTypes,
}: ModalBodyProps) => {
  const {
    opensApplication = false,
    opensAlert = false,
    opensInvestigation = false,
    investigationType = null,
    promotesToCase = false,
    investigationTypeUseCase = null,
    isNewInvestigationType = false,
    investigationError = null,
  } = mutableCurrentEdgeConfig;

  const [typeInputValue, setTypeInputValue] = useState('');
  const [caseCheckboxDisabled, setCaseCheckboxDisabled] = useState(false);

  // Build investigation type options grouped by use case
  const investigationTypeOptions = useMemo(() => {
    if (!investigationTypes || investigationTypes.length === 0) return [];

    const groups = Array.from(new Set(investigationTypes.map(invType => invType.useCase)));
    const options = investigationTypes.reduce(
      (current, next) => {
        const result = current.map(value => {
          const curr = { ...value };
          if (next.useCase === curr.label && curr.options) {
            curr.options.push({ label: next.name, value: next.name });
          }
          return curr;
        });
        return result;
      },
      [
        ...groups.map(name => ({
          label: name,
          value: null,
          options: [] as Option[],
        })),
        {
          label: 'New Type',
          value: null,
          options: [] as Option[],
        },
      ],
    );
    return options;
  }, [investigationTypes]);

  const useCaseList = useMemo(() => {
    if (!investigationTypes || investigationTypes.length === 0) return [];
    const groups = Array.from(new Set(investigationTypes.map(invType => invType.useCase)));
    return groups.map(useCase => ({ label: useCase, value: useCase }));
  }, [investigationTypes]);

  const getSelectedOption = (value: string | null) => {
    if (!value) return [];
    return [{ label: value, value }];
  };

  const createNewType = (inputValue: string) => {
    setMutableCurrentEdgeConfig({
      ...mutableCurrentEdgeConfig,
      investigationType: inputValue,
      isNewInvestigationType: true,
      investigationError: null,
    });
  };

  // Update checkbox disabled state based on investigation config
  useEffect(() => {
    if (opensInvestigation && investigationType) {
      setCaseCheckboxDisabled(false);
    } else {
      setCaseCheckboxDisabled(true);
    }
  }, [opensInvestigation, investigationType]);

  // Check if investigation type is new
  useEffect(() => {
    if (!investigationTypes) return;
    const doesTypeExist = investigationTypes.find(
      inv => inv.name === investigationType,
    );
    const isNewOption = investigationType && !doesTypeExist;
    if (isNewOption !== isNewInvestigationType) {
      setMutableCurrentEdgeConfig({
        ...mutableCurrentEdgeConfig,
        isNewInvestigationType: !!isNewOption,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationTypes, investigationType, isNewInvestigationType]);

  // Get sample edge display with node
  const getSampleEdgeDisplay = () => {
    const nodeIcon = NODE_TYPE_ICON_MAP[node.type] || IconName.Workflows;

    return (
      <Box
        direction="column"
        alignItems="center"
        gap={2}
        style={{ marginBottom: 24, position: 'relative' }}
      >
        <JourneyNode
          iconName={nodeIcon}
          isSelected={false}
          buttonOnClick={noop}
          status={isSourceWorkflow ? 'info' : 'neutral'}
          isNonInteractive
          style={{ width: 180, height: 32 }}
        >
          {node.name}
        </JourneyNode>
        <StyledEdge />
        <JourneyBadge
          outcome={edge.condition || 'Unnamed'}
          style={{ border: '1px solid var(--cl-gray-800)', zIndex: 1 }}
        />
      </Box>
    );
  };

  // For application journeys
  const getOriginationOutcomeCards = () => (
    <Card style={{ width: '100%' }}>
      <Box
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={5}
      >
        <Text weight={700}>Applications</Text>
        <Checkbox
          checked={opensApplication}
          onChange={event => setMutableCurrentEdgeConfig({
            ...mutableCurrentEdgeConfig,
            opensApplication: event.target.checked,
          })}
          label={(
            <Box direction="row">
              <Text>This outcome creates an Application</Text>
              <StyledBadge
                icon={IconName.ApplicationReview}
                iconSize="small"
                color="white"
                style={{ backgroundColor: 'transparent' }}
              />
            </Box>
          )}
          helpText="An Application will be created when this outcome is reached."
        />
      </Box>
    </Card>
  );

  // For alert journeys
  const getOngoingOutcomeCards = () => (
    <>
      <Card style={{ width: '100%' }}>
        <Box
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={5}
        >
          <Text weight={700}>Alerts</Text>
          <Checkbox
            checked={opensAlert}
            onChange={event => setMutableCurrentEdgeConfig({
              ...mutableCurrentEdgeConfig,
              opensAlert: event.target.checked,
            })}
            label={(
              <Box direction="row">
                <Text>This outcome triggers an Alert</Text>
                <StyledBadge
                  icon={IconName.BoltAlert}
                  iconSize="small"
                  color="white"
                  style={{ backgroundColor: 'transparent' }}
                />
              </Box>
            )}
            helpText="An Alert will be triggered when this outcome is reached."
          />
        </Box>
      </Card>

      <Card style={{ minHeight: '257px', width: '100%' }}>
        <Box direction="column" justifyContent="flex-start" alignItems="flex-start" gap={5}>
          <Text weight={700}>Investigations</Text>
          <Checkbox
            checked={opensInvestigation}
            onChange={event => {
              setMutableCurrentEdgeConfig({
                ...mutableCurrentEdgeConfig,
                opensInvestigation: event.target.checked,
                investigationType: !event.target.checked
                  ? null
                  : mutableCurrentEdgeConfig.investigationType,
                promotesToCase: !event.target.checked
                  ? false
                  : mutableCurrentEdgeConfig.promotesToCase,
              });
            }}
            label={(
              <Box direction="row">
                <Text>This outcome triggers an Investigation</Text>
                <StyledBadge
                  icon={IconName.FolderOpenInvestigation}
                  iconSize="small"
                  color="black"
                  style={{ backgroundColor: 'transparent' }}
                />
              </Box>
            )}
            helpText={'An Investigation will open with an "Alert Review" status. '
              + 'Alerts for an entity will aggregate in the Investigation for review.'}
          />
          <Box direction="column" alignItems="flex-start" gap={2}>
            <Box
              direction="row"
              gap={4}
              id="select-investigation-type"
              justifyContent="flex-start"
            >
              <MultiSelect
                data-testid="investigation-type-select"
                required
                label="Select Investigation Type"
                isMulti={false}
                placeholder={'Select an Investigation Type or type to create a '
                  + 'custom Investigation Type'}
                isSearchable
                inputValue={typeInputValue}
                onInputChange={e => {
                  const isValid = e.match(/^[\w&. -]+$/);
                  if (isValid || !e) {
                    setTypeInputValue(e);
                  }
                }}
                options={investigationTypeOptions}
                onChange={(option: any) => setMutableCurrentEdgeConfig({
                  ...mutableCurrentEdgeConfig,
                  investigationType: option.value,
                  investigationError: null,
                })}
                isCreatable={!!investigationTypesCreatePermission}
                onCreateOption={(option: Option) => createNewType(option.label)}
                selectedOptions={getSelectedOption(investigationType)}
                error={investigationError}
                isDisabled={!opensInvestigation}
              />
            </Box>
            {isNewInvestigationType && (
              <>
                <Label
                  style={{ fontWeight: 400, display: 'flex' }}
                  htmlFor="select-investigation-type"
                >
                  <Text style={{ color: 'red' }}>*</Text>Use Case
                  <Tooltip
                    content="This will categorize the Investigation Type"
                    delayDuration={0}
                  >
                    <Icon icon={IconName.Help} size="small" style={{ marginLeft: 4 }} />
                  </Tooltip>
                </Label>
                <Box direction="column" gap={2}>
                  {useCaseList.map(useCase => (
                    <Checkbox
                      key={useCase.value}
                      checked={investigationTypeUseCase === useCase.value}
                      onChange={() => setMutableCurrentEdgeConfig({
                        ...mutableCurrentEdgeConfig,
                        investigationTypeUseCase: useCase.value,
                      })}
                      label={useCase.label}
                      disabled={!opensInvestigation}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Card>

      <Card style={{ width: '100%' }}>
        <Box direction="column" justifyContent="flex-start" alignItems="flex-start" gap={5}>
          <Text weight={700}>Promote Investigation to Case</Text>
          <Checkbox
            checked={promotesToCase}
            disabled={caseCheckboxDisabled}
            onChange={event => setMutableCurrentEdgeConfig({
              ...mutableCurrentEdgeConfig,
              promotesToCase: event.target.checked,
            })}
            label={(
              <Box direction="row">
                <Text>Promote Investigation to a Case automatically</Text>
                <StyledBadge
                  icon={IconName.Escalate}
                  iconSize="small"
                  color="black"
                  style={{ backgroundColor: 'transparent' }}
                />
              </Box>
            )}
            helpText={'If an Investigation is already open for an entity, it will get '
              + 'promoted to a Case if this outcome is reached. If an Investigation is '
              + 'not already open, one will be created.'}
          />
        </Box>
      </Card>
    </>
  );

  return (
    <Box direction="column" justifyContent="flex-start" alignItems="flex-start" gap={3}>
      {getSampleEdgeDisplay()}
      {journeyType === 'alert' ? getOngoingOutcomeCards() : getOriginationOutcomeCards()}
    </Box>
  );
};

/**
 * Outcome Options Modal Component
 *
 * Comprehensive modal for configuring edge options on source workflows.
 * Supports tabs for each outcome and various configuration options.
 */
const OutcomeOptionsModal = ({
  isOpen,
  onClose,
  onConfirm,
  node,
  nodeEdges,
  journeyType = 'application',
}: OutcomeOptionsModalProps) => {
  const { typhonDomain, permissions } = useContext(UserContext);
  const addSnackBar = useSnackBar();

  const initialConfigsByEdge = useMemo(() => getEdgeConfigs(nodeEdges), [nodeEdges]);
  const [edge, setEdge] = useState(nodeEdges[0]);
  const [configsByEdge, setConfigByEdge] = useState<EdgeConfig[]>(initialConfigsByEdge);
  const currentEdgeConfig = configsByEdge.find(edgeConfig => edgeConfig.id === edge.id);
  const [mutableCurrentEdgeConfig, setMutableCurrentEdgeConfig] = useState<EdgeConfig>(
    cloneDeep(currentEdgeConfig) || { id: edge.id },
  );
  const [investigationTypes, setInvestigationTypes] = useState<Array<{
    name: string;
    useCase: string;
  }>>([]);

  const isSourceWorkflow = node.type === 'source_workflow'
    && (node.config as any)?.is_source_workflow;
  const nodeIcon = NODE_TYPE_ICON_MAP[node.type] || IconName.Workflows;
  const workflowVersion = (node.config as any)?.workflow_version;
  // TODO: Determine from workflow metadata
  const isWorkflowActive = true;

  // Fetch investigation types
  const fetchInvestigationTypes = useCallback(async () => {
    try {
      const response = await apiCalls.investigations.getInvestigationTypes({
        typhonDomain,
      });
      setInvestigationTypes(response.data || []);
    } catch (err) {
      // Silently fail - investigation types are optional
    }
  }, [typhonDomain]);

  useEffect(() => {
    if (isOpen && journeyType === 'alert') {
      fetchInvestigationTypes();
    }
  }, [isOpen, journeyType, fetchInvestigationTypes]);

  const formatConfigByEdgeForUpdate = () => {
    const result = [];
    for (let i = 0; i < configsByEdge.length; i += 1) {
      if (configsByEdge[i].id === mutableCurrentEdgeConfig.id) {
        result.push(mutableCurrentEdgeConfig);
      } else {
        result.push(configsByEdge[i]);
      }
    }
    return result;
  };

  const addInvestigationTypeRequest = async () => {
    const payload = {
      name: mutableCurrentEdgeConfig.investigationType,
      use_case: mutableCurrentEdgeConfig.investigationTypeUseCase,
    };
    try {
      await apiCalls.investigations.createInvestigationType({
        typhonDomain,
        payload,
      });
      await fetchInvestigationTypes();
      addSnackBar({ type: 'success', title: 'Investigation Type added.' });
      return true;
    } catch (err: any) {
      let msg = "Couldn't create the Investigation Type.";
      if (err.response && err.response.status === 409) {
        msg = 'This Investigation Type already exists';
        setMutableCurrentEdgeConfig({
          ...mutableCurrentEdgeConfig,
          investigationError: msg,
        });
      } else {
        addSnackBar({ type: 'danger', title: msg });
      }
      return false;
    }
  };

  const onSave = async () => {
    let wasTypeAdded;
    if (mutableCurrentEdgeConfig.isNewInvestigationType) {
      wasTypeAdded = await addInvestigationTypeRequest();
      if (!wasTypeAdded) {
        return;
      }
    }

    onConfirm(formatConfigByEdgeForUpdate());
  };

  const isSaveButtonDisabled = () => {
    const formattedConfigsByEdges = formatConfigByEdgeForUpdate();
    let isDisabled = false;

    formattedConfigsByEdges.forEach(currEdge => {
      const { opensInvestigation, investigationType } = currEdge;

      if (opensInvestigation && !investigationType) {
        isDisabled = true;
      }
    });

    return isDisabled;
  };

  const modalBodyProps = {
    journeyType,
    isSourceWorkflow,
    node,
    edge,
    mutableCurrentEdgeConfig,
    setMutableCurrentEdgeConfig,
    investigationTypesCreatePermission: Boolean(
      permissions?.['CREATE:InvestigationTypes:null'],
    ),
    investigationTypes,
  };

  const tabs = nodeEdges.map(nodeEdge => ({
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <JourneyBadge
          outcome={nodeEdge.condition || 'Unnamed'}
          style={{ border: '1px solid var(--cl-gray-800)' }}
        />
        {` ${nodeEdge.condition || 'Unnamed'}`}
      </div>
    ),
    content: <ModalBody {...modalBodyProps} />,
    value: nodeEdge.id,
  }));

  return (
    <Modal
      isOpen={isOpen}
      hasCloseIcon
      style={{ maxWidth: 'fit-content' }}
      header={(
        <h3 style={{ display: 'flex', alignItems: 'center' }}>
          {'Outcome Options: '}
          <JourneyNode
            style={{ marginLeft: '10px' }}
            iconName={nodeIcon}
            workflowVersionNumber={workflowVersion}
            isWorkflowActive={isWorkflowActive}
            isSelected={false}
            buttonOnClick={noop}
            status={isSourceWorkflow ? 'info' : 'neutral'}
            isNonInteractive
          >
            {node.name}
          </JourneyNode>
        </h3>
      )}
      tertiaryAction={(
        <Box direction="row" justifyContent="flex-start">
          <Icon icon={IconName.Info} />
          <Text>Changes made to outcomes will affect this Journey only.</Text>
        </Box>
      )}
      onClose={onClose}
      secondaryAction={<Button onClick={onClose}>Cancel</Button>}
      primaryAction={(
        <Button onClick={onSave} disabled={isSaveButtonDisabled()}>
          Save
        </Button>
      )}
    >
      <Box direction="row" justifyContent="flex-start">
        <Tabs
          orientation="vertical"
          tabs={tabs}
          listStyle={{ width: 'max-content' }}
          onValueChange={value => {
            const newEdge = nodeEdges.find(nodeEdge => nodeEdge.id === value);
            const newEdgeConfig = configsByEdge.find(edgeConfig => edgeConfig.id === newEdge?.id);
            if (newEdge && newEdgeConfig) {
              setConfigByEdge(formatConfigByEdgeForUpdate());
              setMutableCurrentEdgeConfig(cloneDeep(newEdgeConfig));
              setEdge(newEdge);
            }
          }}
        />
      </Box>
    </Modal>
  );
};

export default OutcomeOptionsModal;
